import { DemographicInputProps, FormLayout } from '../common';
import { formItemLayout } from '../../../common';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import { getFormCheckboxComponent } from '../common/Utils';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { useEffect, useState } from 'react';
import {
  Flex,
  Grid,
  FormControl,
  FormLabel,
  GridItem,
  FormHelperText,
  Checkbox,
} from '@chakra-ui/react';
import { DemographicsInformation } from '@ssar-app/domain';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { ObjectSchema } from 'yup';

export const Demographics = (props: DemographicInputProps) => {
  const { stepKey, validator } = props;
  const { lookupsManager } = props.moduleProps;
  const { isStepActive } = useStepContext();
  const [dataPartState] = useDataPart<DemographicsInformation>(stepKey);
  const [demographicsModel, setDemographicsModel] = useState<
    Partial<DemographicsInformation>
  >(
    {
      shouldAppendDOBCCYYMMDD: false,
      shouldAppendDOBMMDDCCYY: false,
      isEmploymentDataSelected: false,
      isExperianSocialSelected: false,
      isMlaInSelected: false,
      isPhoneNumberDataSelected: false,
      ...dataPartState.data,
    } || {},
  );
  const demographicsCodes = lookupsManager
    .getDemographicsLookups()
    .map((item) => item.code);
  const formMethods = useForm<DemographicsInformation>({
    defaultValues: demographicsModel || {},
    mode: 'onChange',
    resolver: yupResolver(validator),
  });
  const { errors, control, trigger, formState, setValue } = formMethods;

  useEffect(() => {
    if (demographicsModel) {
      setValue(
        'shouldAppendDOBCCYYMMDD',
        demographicsModel.shouldAppendDOBCCYYMMDD,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setValue(
        'shouldAppendDOBMMDDCCYY',
        demographicsModel.shouldAppendDOBMMDDCCYY,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [demographicsModel, setValue]);

  const getDemographicsLookupsName = (code: string) => {
    const demoLookup = lookupsManager
      .getDemographicsLookups()
      .find((a) => a.code === code);
    return demoLookup?.name || demoLookup?.code || '';
  };

  return (
    <>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={[
          'shouldAppendDOBCCYYMMDD',
          'shouldAppendDOBMMDDCCYY',
          'isEmploymentDataSelected',
          'isPhoneNumberDataSelected',
          'isExperianSocialSelected',
          'isMlaInSelected',
        ]}
        validator={validator as ObjectSchema}
        model={demographicsModel}
        isDirty={formState.isDirty}
      />
      {isStepActive && (
        <form {...formItemLayout}>
          <FormLayout header='Demographics'>
            <Flex direction='column' pl='4'>
              <Grid
                templateRows='repeat(5, 1fr)'
                templateColumns='repeat(5, 1fr)'
                gap='1'
                h='44'
              >
                {demographicsCodes.includes('AppendDOB_CCYYMMDD') && (
                  <GridItem width={'96'}>
                    <FormControl
                      isInvalid={errors.shouldAppendDOBCCYYMMDD ? true : false}
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                    >
                      <FormLabel
                        fontSize='md'
                        htmlFor='shouldAppendDOBCCYYMMDD'
                        mt='2'
                      >
                        Date of Birth:
                      </FormLabel>
                      <Controller
                        name='shouldAppendDOBCCYYMMDD'
                        control={control}
                        render={({ value, onChange }) => (
                          <Checkbox
                            onChange={(e) => {
                              onChange(e.target.checked);
                              setDemographicsModel({
                                ...demographicsModel,
                                shouldAppendDOBCCYYMMDD: e.target.checked,
                              });
                            }}
                            disabled={
                              demographicsModel?.shouldAppendDOBMMDDCCYY
                            }
                            checked={value}
                            defaultChecked={
                              demographicsModel?.shouldAppendDOBCCYYMMDD
                            }
                            borderColor={'gray.300'}
                          >
                            {getDemographicsLookupsName('AppendDOB_CCYYMMDD')}
                          </Checkbox>
                        )}
                      ></Controller>
                    </FormControl>
                  </GridItem>
                )}
                {demographicsCodes.includes('AppendDOB_MMDDCCYY') && (
                  <GridItem colSpan={2}>
                    <FormControl
                      isInvalid={errors.shouldAppendDOBMMDDCCYY ? true : false}
                    >
                      <FormLabel> </FormLabel>
                      <Controller
                        name='shouldAppendDOBMMDDCCYY'
                        control={control}
                        render={({ value, onChange }) => (
                          <Checkbox
                            onChange={(e) => {
                              onChange(e.target.checked);
                              setDemographicsModel({
                                ...demographicsModel,
                                shouldAppendDOBMMDDCCYY: e.target.checked,
                              });
                            }}
                            defaultChecked={
                              demographicsModel?.shouldAppendDOBMMDDCCYY
                            }
                            disabled={
                              demographicsModel?.shouldAppendDOBCCYYMMDD
                            }
                            checked={value}
                            borderColor={'gray.300'}
                          >
                            {getDemographicsLookupsName('AppendDOB_MMDDCCYY')}
                          </Checkbox>
                        )}
                      ></Controller>
                    </FormControl>
                  </GridItem>
                )}
                {demographicsCodes.includes('AppendEmplData') && (
                  <GridItem colSpan={3} rowSpan={1}>
                    <FormControl
                      isInvalid={errors.isEmploymentDataSelected ? true : false}
                    >
                      {getFormCheckboxComponent(
                        'isEmploymentDataSelected',
                        control,
                        demographicsModel.isEmploymentDataSelected,
                        getDemographicsLookupsName('AppendEmplData'),
                        (value) => {
                          setValue('isEmploymentDataSelected', value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setDemographicsModel({
                            ...demographicsModel,
                            isEmploymentDataSelected: value,
                          });
                        },
                      )}
                    </FormControl>
                  </GridItem>
                )}
                {demographicsCodes.includes('AppendPhone') && (
                  <GridItem colSpan={3} rowSpan={1}>
                    <FormControl
                      isInvalid={
                        errors.isPhoneNumberDataSelected ? true : false
                      }
                    >
                      {getFormCheckboxComponent(
                        'isPhoneNumberDataSelected',
                        control,
                        demographicsModel.isPhoneNumberDataSelected,
                        getDemographicsLookupsName('AppendPhone'),
                        (value) => {
                          setValue('isPhoneNumberDataSelected', value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setDemographicsModel({
                            ...demographicsModel,
                            isPhoneNumberDataSelected: value,
                          });
                        },
                      )}
                    </FormControl>
                  </GridItem>
                )}
                {demographicsCodes.includes('AppendExpSSN') && (
                  <GridItem colSpan={3} rowSpan={1}>
                    <FormControl
                      isInvalid={errors.isExperianSocialSelected ? true : false}
                    >
                      {getFormCheckboxComponent(
                        'isExperianSocialSelected',
                        control,
                        demographicsModel.isExperianSocialSelected,
                        getDemographicsLookupsName('AppendExpSSN'),
                        (value) => {
                          setValue('isExperianSocialSelected', value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setDemographicsModel({
                            ...demographicsModel,
                            isExperianSocialSelected: value,
                          });
                        },
                      )}
                    </FormControl>
                  </GridItem>
                )}
                {demographicsCodes.includes('AppendMLA') && (
                  <GridItem colSpan={3} rowSpan={1}>
                    <FormControl
                      isInvalid={errors?.isMlaInSelected ? true : false}
                    >
                      {getFormCheckboxComponent(
                        'isMlaInSelected',
                        control,
                        demographicsModel.isMlaInSelected,
                        getDemographicsLookupsName('AppendMLA'),
                        (value) => {
                          setValue('isMlaInSelected', value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setDemographicsModel({
                            ...demographicsModel,
                            isMlaInSelected: value,
                          });
                        },
                      )}
                    </FormControl>
                  </GridItem>
                )}
              </Grid>
              <FormControl my={2}>
                <FormHelperText color='orange.500'>
                  {`Please note that Demographics may not be the only selected data within the program. At least one element from either the "Scores" or the "Attributes" tab must be selected.`}
                </FormHelperText>
              </FormControl>
            </Flex>
          </FormLayout>
        </form>
      )}
    </>
  );
};
